(ns net.kestec.mhda-pilgrimage.cms.editor
  (:require
    ["@mui/icons-material/Save" :default SaveIcon]
    ["@mui/icons-material/Shortcut" :default ShortcutIcon]
    ["@mui/material/Button" :default Button]
    ["@mui/material/IconButton" :default IconButton]
    ["@mui/material/InputAdornment" :default InputAdornment]
    ["@mui/material/Paper" :default Paper]
    ["@mui/material/Popper" :default Popper]
    ["@mui/material/TextField" :default TextField]
    ["@mui/material/Toolbar" :default Toolbar]
    ["@tiptap/extension-file-handler" :default FileHandlerExtension]
    ["@tiptap/extension-image" :default ImageExtension]
    ["@tiptap/react" :refer [useEditor EditorContent]]
    ["@tiptap/starter-kit" :default StarterKitExtension]
    [ajax.core :as ajax]
    [clojure.string :as str]
    [net.kestec.mhda-pilgrimage.cms.cms :as cms]
    [net.kestec.re-frame.event :as event]
    [net.kestec.re-frame.subscription :as subscription]
    [net.kestec.mhda-pilgrimage.db :as db]
    [re-frame.core :as rf]
    [reagent.core :as r]))

(rf/reg-event-db
  ::create-instance
  (fn [db [_ editor-id editor]]
    (assoc db
      ::instance-id editor-id
      ::instance editor)))

(rf/reg-event-fx
  ::command
  (fn [{{:as db ::keys [^js instance]} :db} [_ action]]
    {::command {:instance instance :action action}
     :db       (update db ::selection (fnil inc 0))}))

(rf/reg-fx
  ::command
  (fn [{:keys [instance action]}]
    #_(log/info "Command" instance action)
    (-> instance
        (.chain)
        (.focus)
        (action instance)
        (.run))))

(rf/reg-sub
  ::active?
  :<- [::db/get ::selection]
  :<- [::db/get ::instance]
  (fn [[_sel ^js inst] [_ active arg]]
    (cond (or (not active)
              (not inst))
          nil
          (if arg
            (.isActive inst active (clj->js arg))
            (.isActive inst active))
          true
          :else false)))

(rf/reg-sub
  ::button-variant-for
  (fn [[_ active arg]]
    (rf/subscribe [::active? active arg]))
  (fn [active _]
    (case active
      nil "text"
      true "contained"
      false "outlined")))

(defn default-button [& {:as props
                         :keys [label icon]
                         ::event/keys [click]}]
  [:> Button (merge {:start-icon (r/as-element icon)
                     :on-click   (event/handler click)}
                    (dissoc props :label :icon :on-click))
   label])

(let [el (r/atom nil)]
  (defn- anchor-button [& ui-props]
    [:<>
     (into [default-button
            :ref #(reset! el %)]
           ui-props)
     [(fn []
        (let [value (when-let [ed (do (subscription/get! ::db/get ::selection)
                                      (subscription/get! ::db/get ::instance))]
                      (.. ^js ed
                          (getAttributes "link")
                          -href))]
          [:> Popper {:open      (boolean (subscription/get! ::active? "link"))
                      :anchor-el @el
                      :placement :top}
           [:> Paper {:style {:padding "0.2em"}}
            [:> TextField {:variant    :standard
                           :auto-focus false
                           :label      "Link target"
                           :value      value
                           ;; :value @name-ref
                           :on-change  (when-let [ed (subscription/get! ::db/get ::instance)]
                                         #(do
                                            (.. ^js ed chain ;; focus
                                                (extendMarkRange "link")
                                                (setLink #js{:href (.. % -target -value)})
                                                run)
                                            (rf/dispatch [::db/update! [::selection] (fnil inc 0)])))
                           :InputProps
                           #js{:endAdornment
                               (r/as-element
                                 [:> InputAdornment
                                  {:position :end}
                                  [:> IconButton
                                   {:aria-label "Follow link"
                                    :href       value
                                    :target     "_blank"}
                                   [:> ShortcutIcon]]])}}]]]))]]))

(rf/reg-event-fx
  ::file-upload
  (fn [{:keys [db]} [_ file & {::event/keys [complete]}]]
    {:http-xhrio {:method :post
                  :uri "/api/cms"
                  :body file
                  :headers {"Content-Type" (.-type file)
                            "X-File-Name" (.-name file)}
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      (into complete [(.-name file) (.-type file)])
                  :on-failure      [::event/log :error]}}))

(rf/reg-event-fx
  ::embed-file
  (fn [{:keys [db]} [_ current-editor pos file-name mime-type href]]
    {::insert-content {:current-editor current-editor
                       :pos pos
                       :insert-arg (case mime-type
                                     "application/pdf" #js{:type "text"
                                                           :marks #js[#js{:type "link"
                                                                          :attrs #js{:href href}}]
                                                           :text (str "📄 " file-name)}
                                     #js{:type "image"
                                         :attrs #js{:src href}})}}))

(rf/reg-fx
  ::insert-content
  (fn [{:keys [current-editor pos insert-arg]}]
    (.. ^js current-editor
        (chain)
        (insertContentAt pos insert-arg)
        (focus)
        (run))))

(rf/reg-event-fx
  ::save
  (fn [{{::keys [^js instance]} :db} [_ name storage-path]]
    {:http-xhrio {:method :post
                  :uri storage-path
                  :body (.getHTML instance)
                  :headers {"Content-Type" "text/html"
                            "X-File-Name" name}
                  :response-format (ajax/json-response-format {:keywords? true})
                  :on-success      [::store-to storage-path name]
                  :on-failure      [::event/log :error]}}))

(rf/reg-event-fx
  ::store-to
  (fn [{{::keys [^js instance]} :db} [_ storage-path name file-url]]
    (let [[_ file-id] (re-matches #"/api/cms/(.*)" file-url)]
      {:http-xhrio {:method :put
                    :uri storage-path
                    :body file-id
                    :headers {"Content-Type" "text/plain"}
                    :response-format (ajax/text-response-format)
                    :on-success      [::event/log :info]
                    :on-failure      [::event/log :error]}})))

(defn editor* [editor-id init-content]
  (let [editor (useEditor
                 #js{:extensions [(.configure StarterKitExtension #js{:link #js {:openOnClick false}})
                                  ImageExtension
                                  (.configure FileHandlerExtension
                                              #js{:allowedMimeTypes #js["image/png" "image/jpeg" "image/gif" "image/webp" "application/pdf"]
                                                  :onDrop (fn [^js current-editor files pos]
                                                            (doseq [file files]
                                                              (rf/dispatch [::file-upload file
                                                                            ::event/complete [::embed-file current-editor pos]])))
                                                  :onPaste (fn [^js current-editor files html-content]
                                                             ;; if there is htmlContent, stop manual insertion & let other extensions handle insertion via inputRule
                                                             ;; you could extract the pasted file from this url string and upload it to a server for example
                                                             (when (str/blank? html-content)
                                                               (doseq [file files]
                                                                 (rf/dispatch [::file-upload file
                                                                               ::event/complete [::embed-file current-editor (.. current-editor -state -selection -anchor)]]))))})]
                     :content init-content
                     :onSelectionUpdate (fn [_e]
                                          (rf/dispatch [::db/update! [::selection] (fnil inc 0)]))
                     :onCreate          (fn [^js e]
                                          (rf/dispatch [::create-instance editor-id (.-editor e)]))}
                 #js[editor-id])]
    [:> EditorContent {:editor editor}]))

(defn editor [editor-id storage-path]
  [:<>
   (into
     [:> Toolbar {:variant "dense"}]
     (map (fn [{:keys [label action active active-arg button]}]
            (-> (or button [default-button])
                (into [:variant (subscription/get! ::button-variant-for active active-arg)
                       ::event/click [::command action]
                       :size "small"
                       :label label]))))
     [{:label      [:h3 {:style {:margin "2px" :height "25px"}} "H3"]
       :action     #(.setHeading ^js % #js{:level 3})
       :active     "heading"
       :active-arg {:level 3}}
      {:label      [:h4 {:style {:margin "2px" :height "25px"}} "H4"]
       :action     #(.setHeading ^js % #js{:level 4})
       :active     "heading"
       :active-arg {:level 4}}
      {:label      [:h5 {:style {:margin "2px" :height "25px"}} "H5"]
       :action     #(.setHeading ^js % #js{:level 5})
       :active     "heading"
       :active-arg {:level 5}}
      {:label      [:h6 {:style {:margin "2px" :height "25px"}} "H6"]
       :action     #(.setHeading ^js % #js{:level 6})
       :active     "heading"
       :active-arg {:level 6}}
      {:label      [:div {:style {:margin "2px" :height "25px"}} "Block"]
       :active     "paragraph"
       :action     #(.setParagraph ^js %)
       :active-arg {}}
      {:label      [:div {:style {:margin "2px" :height "25px"}} "Zitat"]
       :active     "blockquote"
       :action     #(.toggleBlockquote ^js %)
       :active-arg {}}
      {:label      [:div {:style {:margin "2px" :height "25px"}} "Code"]
       :active     "code"
       :action     #(.toggleCode ^js %)
       :active-arg {}}
      {:label      [:div {:style {:margin "2px" :height "25px"}} [:b "Fett"]]
       :action     #(.toggleBold ^js %)
       :active-arg {}
       :active     "bold"}
      {:label      [:div {:style {:margin "2px" :height "25px"}} [:i "Italic"]]
       :action     #(.toggleItalic ^js %)
       :active     "italic"
       :active-arg {}}
      {:label      [:div {:style {:margin "2px" :height "25px"}} [:a "Link"]]
       :action     #(.. ^js %
                        (extendMarkRange "link")
                        (toggleLink #js{:href (str (.. ^js %2
                                                       (getAttributes "link")
                                                       -href))}))
       :active     "link"
       :button     [anchor-button]
       :active-arg {}}
      {:label      [:div {:style {:margin "2px" :height "25px"}} "- Punktierung"]
       :action     #(.toggleBulletList ^js %)
       :active     "bulletList"
       :active-arg {}}
      {:label      [:div {:style {:margin "2px" :height "25px"}} "1. Aufzählung"]
       :action     #(.toggleOrderedList ^js %)
       :active     "orderedList"
       :active-arg {}}])
   [:> Paper {:style {:min-height "120px" :padding "7px" :margin-bottom "5px" :background-color "#f1f1f1"}}
    (if-let [init-content @(rf/subscribe [::cms/storage-content storage-path])]
      [:f> editor* editor-id init-content]
      [:div "Loading editor content ..."])]
   [default-button
    :variant "contained"
    ::event/click [::save editor-id storage-path]
    :size "small"
    :startIcon (r/as-element [:> SaveIcon])
    :label "Speichern"]])
